import * as React from 'react';
import { Box, Flex } from '@chakra-ui/react';
import {
  Card,
  HStack,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';

type SpecimenProps = {
  name: string;
  note?: string;
  /** Marks a legacy/deprecated specimen so the audit surfaces it. */
  flagged?: boolean;
  /** Recency marker — see ChangeBadge. */
  badge?: React.ReactNode;
  children: React.ReactNode;
};

/**
 * One card per component. All of a component's variants/states live inside
 * a single Specimen (as `State` cells) instead of each state getting its
 * own card — so the page reads as "here is Component X and everything it
 * can do", not a collage of disconnected tiles.
 */
export const Specimen = ({
  name,
  note,
  flagged,
  badge,
  children,
}: SpecimenProps) => (
  <Card
    p={4}
    borderColor={flagged ? 'orange' : 'cloud'}
    borderWidth={flagged ? '2px' : '1px'}
  >
    <VStack gap={3} align="stretch">
      <VStack gap={0} align="stretch">
        <HStack gap={2} align="center">
          <Text fontSize="sm" fontWeight="bold" color="muted.900">
            {name}
          </Text>
          {badge}
        </HStack>
        {note && (
          <Text fontSize="xs" color="muted.400">
            {note}
          </Text>
        )}
      </VStack>

      <Box borderTopWidth="1px" borderColor="cloud" pt={4}>
        <Flex wrap="wrap" gap={6} align="flex-end">
          {children}
        </Flex>
      </Box>
    </VStack>
  </Card>
);

type StateProps = {
  /** State / variant name, e.g. "invalid", "disabled", "checked". */
  label?: string;
  /** Set for controls that need room (inputs, selects, cards). */
  wide?: boolean;
  children: React.ReactNode;
};

/**
 * A single state/variant cell inside a Specimen: the rendered component
 * above, its state label below.
 */
export const State = ({ label, wide, children }: StateProps) => (
  <VStack
    gap={2}
    align="flex-start"
    minW={wide ? '240px' : '110px'}
    flex={wide ? '1 1 240px' : '0 0 auto'}
  >
    <Box w="100%">{children}</Box>
    {label && (
      <Text fontSize="2xs" color="muted.400" fontFamily="mono">
        {label}
      </Text>
    )}
  </VStack>
);
